import type { platformMessagesDe } from "./messages.platform.de";

/**
 * English messages of the operator area (F-69), merged into the English dictionary; every key of
 * {@link platformMessagesDe} is required.
 */
export const platformMessagesEn: Readonly<Record<keyof typeof platformMessagesDe, string>> = {
  "platform.brand": "Zeitavo operations",
  "platform.nav.main": "Navigation of the operator area",
  "platform.nav.overview": "Overview",
  "platform.nav.tenants": "Tenants",
  "platform.nav.onboarding": "Onboard",
  "platform.nav.signups": "Registrations",
  "platform.nav.account": "Account",
  "platform.status.Trial": "Trial",
  "platform.status.Active": "Active",
  "platform.status.PastDue": "Payment overdue",
  "platform.status.Suspended": "Suspended",
  "platform.status.Terminated": "Terminated",
  "platform.status.ExportWindow": "Export period",
  "platform.status.Deleted": "Deleted",
  "platform.isolation.Pool": "Shared database",
  "platform.isolation.Silo": "Own database",
  "platform.lifecycleReason.OperatorDecision": "Decision of the operator",
  "platform.lifecycleReason.SubscriptionStarted": "Subscription started",
  "platform.lifecycleReason.PaymentFailed": "Payment failed",
  "platform.lifecycleReason.PaymentReceived": "Payment received",
  "platform.lifecycleReason.DunningPeriodExpired": "Dunning period expired",
  "platform.lifecycleReason.ContractEnded": "Contract ended",
  "platform.lifecycleReason.Reactivated": "Reactivated",
  "platform.lifecycleReason.ExportWindowExpired": "Export period expired",
  "platform.overview.title": "Overview",
  "platform.overview.byStatus": "Tenants by status",
  "platform.overview.noTenants": "No tenants yet.",
  "platform.overview.tenantCount.one": "1 tenant",
  "platform.overview.tenantCount.other": "{count} tenants",
  "platform.overview.trialsEnding": "Trials ending soon",
  "platform.overview.noTrialsEnding": "No trial ends within the next 7 days.",
  "platform.overview.trialEndsOn": "ends on {date}",
  "platform.overview.pastDue": "Overdue payments",
  "platform.overview.noPastDue": "No overdue payments.",
  "platform.overview.pastDueSince": "overdue since {date}",
  "platform.overview.failedSignups": "Registrations with an error",
  "platform.overview.noFailedSignups": "No registration with an error.",
  "platform.overview.stalledOnboardings": "Stalled onboardings",
  "platform.overview.noStalledOnboardings": "No stalled onboarding.",
  "platform.overview.requestedAt": "registered on {date}",
  "platform.tenants.title": "Tenants",
  "platform.tenants.count.one": "1 tenant found.",
  "platform.tenants.count.other": "{count} tenants found.",
  "platform.tenants.caption": "Tenants of the operator",
  "platform.tenants.empty": "No tenant matches the search and the filter.",
  "platform.tenants.none": "No tenants yet.",
  "platform.tenants.filter": "Search and filter tenants",
  "platform.tenants.search": "Name or identifier",
  "platform.tenants.allStatuses": "All statuses",
  "platform.tenants.allPlans": "All plans",
  "platform.tenants.allDatabases": "All databases",
  "platform.tenant.title": "Tenant",
  "platform.tenant.back": "Back to the tenants",
  "platform.tenant.name": "Name",
  "platform.tenant.status": "Status",
  "platform.tenant.plan": "Plan",
  "platform.tenant.database": "Database",
  "platform.tenant.createdAt": "Created on",
  "platform.tenant.masterData": "Master data",
  "platform.tenant.since": "since {date}",
  "platform.tenant.trialEndsOn": "Trial ends on",
  "platform.tenant.moving": "(move into the database {key} running)",
  "platform.tenant.organization": "Organization in the sign-in service",
  "platform.tenant.noOrganization": "None",
  "platform.tenant.id": "Identifier",
  "platform.tenant.onboarding": "View the state of the onboarding",
  "platform.changes.title": "Change plan and status",
  "platform.changes.none":
    "The contract has ended; plan and status now change only through the periods.",
  "platform.change.reason": "Reason",
  "platform.change.reasonHint":
    "Kept with the change in the platform log. Please enter no data of the customer's employees.",
  "platform.change.reasonBlank": "Please enter a reason, not only spaces.",
  "platform.change.confirmName": "Name of the tenant to confirm",
  "platform.change.confirmNameHint": "Type “{name}” exactly.",
  "platform.change.nameMismatch": "The name does not match the name of the tenant.",
  "platform.change.statusDone": "Status changed: {status}.",
  "platform.action.activate": "Activate",
  "platform.action.activate.consequence":
    "The tenant becomes active. From the export period, the planned deletion is cancelled. The data processing agreement in force must be accepted.",
  "platform.action.mark-past-due": "Mark payment as overdue",
  "platform.action.mark-past-due.consequence":
    "The administrators of the tenant see a notice about the open payment; every function stays available.",
  "platform.action.suspend": "Suspend",
  "platform.action.suspend.consequence":
    "The administration of the tenant becomes read-only. Clocking, own times and exports keep working.",
  "platform.action.terminate": "Terminate",
  "platform.action.terminate.consequence":
    "The contract ends at once and the export period begins. Afterwards the data of the tenant is deleted unless it is reactivated.",
  "platform.plan.change": "Change plan",
  "platform.plan.changed": "Plan changed: {plan}.",
  "platform.plan.new": "New plan",
  "platform.plan.choose": "Choose a plan",
  "platform.plan.option": "{name} ({code})",
  "platform.lifecycle.title": "Status changes",
  "platform.lifecycle.empty": "No status change yet.",
  "platform.lifecycle.change": "{from} → {to}",
  "platform.lifecycle.details": "{reason}, {at}, by {actor}",
  "platform.usage.title": "Usage",
  "platform.usage.empty": "No measured usage yet.",
  "platform.usage.month": "Month",
  "platform.usage.active": "Active employees",
  "platform.usage.employees": "Employees",
  "platform.contacts.title": "Contacts",
  "platform.contacts.empty": "The tenant has not entered contacts yet.",
  "platform.legal.title": "Accepted legal documents",
  "platform.legal.empty": "No decision on legal documents yet.",
  "platform.legal.Accepted": "{document}, version {version}: accepted on {at}",
  "platform.legal.Declined": "{document}, version {version}: declined on {at}",
  "platform.protection.title": "Protection need",
  "platform.protection.none": "The protection need has not been assessed yet.",
  "platform.protection.result": "Protection need {need}; recommended: {isolation}.",
  "platform.protection.need.High": "high",
  "platform.protection.need.VeryHigh": "very high",
  "platform.protection.questions": "Questions on the protection need",
  "platform.protection.publicSector": "Public body",
  "platform.protection.healthCare": "Health care",
  "platform.protection.criticalInfrastructure": "Critical infrastructure",
  "platform.protection.employees": "Number of employees",
  "platform.protection.employeeCount.one": "1 employee",
  "platform.protection.employeeCount.other": "{count} employees",
  "platform.protection.assess": "Assess protection need",
  "platform.protection.save": "Save assessment",
  "platform.protection.saved": "Protection need saved.",
  "platform.sso.title": "Single sign-on",
  "platform.sso.none": "No single sign-on configured.",
  "platform.sso.configure": "Configure single sign-on",
  "platform.sso.change": "Change single sign-on",
  "platform.sso.save": "Save single sign-on",
  "platform.sso.saved": "Single sign-on saved.",
  "platform.sso.protocol": "Protocol",
  "platform.sso.protocol.Oidc": "OpenID Connect",
  "platform.sso.protocol.Saml": "SAML",
  "platform.sso.metadataUrl": "Address of the metadata",
  "platform.sso.metadataUrlHint.Oidc":
    "The discovery document of the customer, usually ending in /.well-known/openid-configuration.",
  "platform.sso.metadataUrlHint.Saml": "The address of the customer's SAML metadata.",
  "platform.sso.clientId": "Client identifier at the customer",
  "platform.sso.clientSecret": "Client secret",
  "platform.sso.clientSecretHint": "Needed on every change; it is neither stored nor shown again.",
  "platform.sso.domains": "E-mail domains",
  "platform.sso.domainsHint":
    "Domains of the customer checked beforehand, separated by commas, for example customer.de, customer.com.",
  "platform.sso.configuredAt": "Configured on",
  "platform.emergency.title": "Emergency accesses",
  "platform.emergency.empty": "No emergency access requested yet.",
  "platform.emergency.item": "{status}: {hours} h, requested on {at}",
  "platform.emergency.validUntil": "Valid until {until}",
  "platform.emergency.status.Requested": "Awaiting approval",
  "platform.emergency.status.Approved": "Approved",
  "platform.emergency.status.Rejected": "Rejected",
  "platform.emergency.status.Revoked": "Revoked",
  "platform.emergency.request": "Request emergency access",
  "platform.emergency.requested":
    "Emergency access requested; the administrators of the tenant decide.",
  "platform.emergency.reason": "Reason",
  "platform.emergency.reasonHint":
    "The administrators of the tenant see the reason, for example the number of the support request.",
  "platform.emergency.hours": "Duration in hours",
  "platform.emergency.requestedBy": "Your name for the tenant",
  "platform.emergency.requestedByHint":
    "The administrators of the tenant see the name with the request.",
  "platform.emergency.send": "Send request",
  "platform.onboarding.title": "Onboard a tenant",
  "platform.onboarding.intro":
    "Creates the organization, the first establishment and the first administrator; the invitation goes to the given address.",
  "platform.onboarding.customer": "Customer",
  "platform.onboarding.tenantName": "Company name with legal form",
  "platform.onboarding.establishment": "First establishment",
  "platform.onboarding.establishmentName": "Name of the establishment",
  "platform.onboarding.federalState": "Federal state",
  "platform.onboarding.chooseState": "Choose a federal state",
  "platform.onboarding.timeZone": "Time zone",
  "platform.onboarding.timeZoneHint": "IANA time zone, in Germany Europe/Berlin.",
  "platform.onboarding.timeZoneInvalid": "This time zone is not known.",
  "platform.onboarding.administrator": "First administrator",
  "platform.onboarding.email": "E-mail address",
  "platform.onboarding.emailHint": "The invitation to set the password goes to this address.",
  "platform.onboarding.firstName": "First name",
  "platform.onboarding.lastName": "Last name",
  "platform.onboarding.database": "Database",
  "platform.onboarding.databaseHint":
    "A very high protection need requires an own database, which must be set up in operations.",
  "platform.onboarding.pool": "Shared database",
  "platform.onboarding.silo": "Own database {key}",
  "platform.onboarding.resume": "Send the form again to resume the interrupted onboarding.",
  "platform.onboarding.start": "Onboard tenant",
  "platform.onboarding.running": "Onboarding …",
  "platform.onboarding.started": "Onboarding of {name} started.",
  "platform.onboarding.statusTitle": "State of the onboarding",
  "platform.onboarding.statusOf": "Onboarding of {name}",
  "platform.onboarding.steps": "Steps",
  "platform.onboarding.status.InProgress": "Interrupted or running",
  "platform.onboarding.status.Completed": "Completed",
  "platform.onboarding.status.RolledBack": "Rolled back",
  "platform.onboarding.step.Started": "Request accepted",
  "platform.onboarding.step.OrganizationCreated": "Organization created in the sign-in service",
  "platform.onboarding.step.AdministratorCreated": "Administrator created",
  "platform.onboarding.step.TenantProvisioned": "Tenant with establishment created",
  "platform.onboarding.step.Registered": "Entered in the tenant catalog",
  "platform.onboarding.stepDone": "done",
  "platform.onboarding.stepOpen": "open",
  "platform.onboarding.lastError": "Last error: {error}",
  "platform.onboarding.times": "Started on {started}, last changed on {updated}.",
  "platform.onboarding.invitation": "Invitation",
  "platform.onboarding.invitationNotSent": "The invitation has not been sent yet.",
  "platform.onboarding.invitationSent": "Last sent on {at}.",
  "platform.onboarding.resend": "Send invitation again",
  "platform.onboarding.resent": "Invitation sent again.",
  "platform.onboarding.openTenant": "Open tenant",
  "platform.signups.title": "Registrations",
  "platform.signups.intro":
    "Companies that registered themselves. An error while creating the tenant remains until the person opens the confirmation link again.",
  "platform.signups.company": "Company",
  "platform.signups.contact": "Contact and postal code",
  "platform.signups.status": "State",
  "platform.signups.status.PendingVerification": "Awaiting confirmation",
  "platform.signups.status.Completed": "Tenant created",
  "platform.signups.status.Duplicate": "Already registered",
  "platform.signups.requestedAt": "Registered on",
  "platform.signups.lastError": "Last error",
  "platform.signups.empty": "No registrations yet.",
  "platform.account.title": "Account",
  "platform.account.manage": "Role: operations (changes)",
  "platform.account.read": "Role: support (read only)",
  "platform.access.title": "The area could not be opened",
  "platform.access.text":
    "The app asks the server whether your account belongs to the operator area and opens the matching area only after its answer.",
  "platform.access.offline":
    "You are offline. As soon as there is a connection, the app tries again.",
  "platform.unsent.one":
    "{count} time entry on this device has not been sent to the server yet. The operator area does not send it; signing out deletes it.",
  "platform.unsent.other":
    "{count} time entries on this device have not been sent to the server yet. The operator area does not send them; signing out deletes them.",
  "platform.nav.billing": "Billing",
  "platform.nav.legalDocuments": "Legal documents",
  "platform.nav.subprocessors": "Subprocessors",
  "platform.nav.securityIncidents": "Security incidents",
  "platform.nav.contractEnd": "End of contract",
  "platform.nav.databases": "Technology",
  "platform.overview.more": "More areas",
  "platform.tenant.billing": "View billing",
  "platform.tenant.contractEnd": "View end of contract",
  "platform.picker.title": "Tenant",
  "platform.picker.tenant": "Tenant",
  "platform.picker.choose": "Choose a tenant",
  "platform.picker.option": "{name} ({status})",
  "platform.billing.title": "Billing",
  "platform.billing.intro":
    "Payment state, invoices and credit notes of a tenant. The reconciliation checks every tenant at the payment provider.",
  "platform.billing.choose": "Choose a tenant to see its payment state and invoices.",
  "platform.reconcile.title": "Reconciliation with the payment provider",
  "platform.reconcile.hint":
    "Compares the subscriptions of every tenant with the payment provider and takes over its state. The reconciliation also runs every night.",
  "platform.reconcile.start": "Reconcile payments",
  "platform.reconcile.done": "Reconciliation completed.",
  "platform.reconcile.checked.one": "1 tenant checked.",
  "platform.reconcile.checked.other": "{count} tenants checked.",
  "platform.reconcile.mismatches": "Differences",
  "platform.reconcile.noMismatches": "No differences.",
  "platform.reconcile.failed": "Not reconciled: {tenants}",
  "platform.payment.title": "Payment state",
  "platform.payment.none": "The tenant has no account at the payment provider.",
  "platform.payment.noSubscription": "No subscription",
  "platform.payment.status": "Subscription",
  "platform.payment.plan": "Plan at the payment provider",
  "platform.payment.periodEnd": "Billing period until",
  "platform.payment.cancelAt": "Subscription ends on",
  "platform.payment.method": "Payment method",
  "platform.payment.customer": "Customer number at the payment provider",
  "platform.payment.syncedAt": "Last reconciled",
  "platform.vat.title": "VAT number",
  "platform.vat.hint":
    "Checks the VAT number of the tenant's billing profile with the EU validation service (VIES) and stores the confirmation.",
  "platform.vat.check": "Check VAT number",
  "platform.vat.valid": "Valid, confirmed on {at} (reference {reference}).",
  "platform.vat.invalid": "Not confirmed, checked on {at}.",
  "platform.vat.validShort": "VAT number confirmed.",
  "platform.vat.invalidShort": "VAT number not confirmed.",
  "platform.invoices.kind": "Kind",
  "platform.invoices.actions": "Actions",
  "platform.creditNote.open": "Credit note for {number}",
  "platform.creditNote.title": "Issue a credit note for {number}",
  "platform.creditNote.reason": "Reason for the credit note",
  "platform.creditNote.reasonHint": "Printed on the credit note the customer receives.",
  "platform.creditNote.amount": "Net amount in euros",
  "platform.creditNote.amountHint":
    "Leave empty to credit the rest of the invoice that has not been credited yet.",
  "platform.creditNote.amountInvalid": "Please enter an amount above 0, for example 12.50.",
  "platform.creditNote.confirm":
    "The credit note gets its own number, is e-mailed to the customer and cannot be withdrawn.",
  "platform.creditNote.issue": "Issue credit note",
  "platform.creditNote.issued": "Credit note {number} issued.",
  "platform.legalDocuments.title": "Legal documents",
  "platform.legalDocuments.intro":
    "Published versions of the terms, the data processing agreement, the service level agreement and the privacy notice. A version cannot be changed; a mistake in the text means a new version.",
  "platform.legalDocuments.list": "Published versions",
  "platform.legalDocuments.empty": "No version published yet.",
  "platform.legalDocuments.new": "Publish a new version",
  "platform.legalDocuments.kind": "Document",
  "platform.legalDocuments.chooseKind": "Choose a document",
  "platform.legalDocuments.version": "Version",
  "platform.legalDocuments.versionHint": "Label of the version, for example 2026-11.",
  "platform.legalDocuments.validFrom": "Valid from",
  "platform.legalDocuments.validFromHint":
    "Today or later (German time). Keep the notice period of the amendment clause.",
  "platform.legalDocuments.documentRef": "Location of the text",
  "platform.legalDocuments.documentRefHint":
    "An https address or a path of the website starting with /, for example /legal/avv-2026-11.pdf.",
  "platform.legalDocuments.file": "File for the checksum",
  "platform.legalDocuments.fileHint":
    "Choose exactly the published file; the checksum is computed in your browser and the file is not uploaded.",
  "platform.legalDocuments.fileFailed":
    "The file could not be read. Compute the checksum with sha256sum and enter it.",
  "platform.legalDocuments.contentHash": "Checksum (SHA-256)",
  "platform.legalDocuments.contentHashHint":
    "64 characters of 0–9 and a–f, as printed by sha256sum.",
  "platform.legalDocuments.showPreview": "Show preview",
  "platform.legalDocuments.preview": "Preview of the new version",
  "platform.legalDocuments.consequence.accept":
    "From {date}, the administrators of every tenant see this version after signing in and accept or decline it.",
  "platform.legalDocuments.consequence.inform":
    "This version applies from {date}; it is for information only and needs no consent.",
  "platform.legalDocuments.confirm":
    "I have checked text, checksum and date. The version cannot be changed after its publication.",
  "platform.legalDocuments.publish": "Publish",
  "platform.legalDocuments.validFromPast":
    "“Valid from” lies before today (German time). Go back to the input and choose today or later.",
  "platform.legalDocuments.back": "Back to the input",
  "platform.legalDocuments.published": "{document}, version {version}, published.",
  "platform.legalDocuments.acceptance": "Consent",
  "platform.legalDocuments.mustAccept": "required",
  "platform.legalDocuments.informOnly": "information only",
  "platform.subprocessors.title": "Subprocessors",
  "platform.subprocessors.intro":
    "The register customers see, with announced changes and history. Every change is announced to every tenant; it applies at the earliest after the objection period.",
  "platform.subprocessors.current": "Current",
  "platform.subprocessors.upcoming": "Announced",
  "platform.subprocessors.history": "History",
  "platform.subprocessors.noneCurrent": "No subprocessors registered.",
  "platform.subprocessors.noneUpcoming": "No change announced.",
  "platform.subprocessors.noneHistory": "No history yet.",
  "platform.subprocessors.kind.Added": "new",
  "platform.subprocessors.kind.Changed": "changed",
  "platform.subprocessors.kind.Removed": "removed",
  "platform.subprocessors.name": "Name of the company",
  "platform.subprocessors.purpose": "Purpose",
  "platform.subprocessors.dataCategories": "Data categories",
  "platform.subprocessors.dataCategoriesHint": "Which personal data the subprocessor receives.",
  "platform.subprocessors.location": "Place of processing",
  "platform.subprocessors.transferSafeguard": "Safeguard for a third country",
  "platform.subprocessors.transferSafeguardHint":
    "For example standard contractual clauses; leave empty for processing in the EU only.",
  "platform.subprocessors.euOnly": "Processing in the EU only",
  "platform.subprocessors.effectiveFrom": "Applies from",
  "platform.subprocessors.effectiveFromHint":
    "At the earliest after the objection period of at least 30 days from today.",
  "platform.subprocessors.announcedAt": "Announced on",
  "platform.subprocessors.add": "Add subprocessor",
  "platform.subprocessors.change": "Change {name}",
  "platform.subprocessors.remove": "Remove {name}",
  "platform.subprocessors.confirmAnnouncement":
    "Every tenant is notified by e-mail and may object within the period.",
  "platform.subprocessors.announce": "Announce change",
  "platform.subprocessors.announced": "Change of {name} announced.",
  "platform.subprocessors.removedFrom": "No longer engaged from",
  "platform.subprocessors.removedFromHint": "Today or later.",
  "platform.subprocessors.removedFromPast": "The date lies before today. Choose today or later.",
  "platform.subprocessors.effectiveFromTooEarly":
    "The date lies within the objection period. Choose a later day.",
  "platform.subprocessors.confirmRemoval":
    "Every tenant is notified by e-mail that the subprocessor is no longer engaged.",
  "platform.subprocessors.removeConfirm": "Announce removal",
  "platform.subprocessors.removed": "End of the engagement of {name} announced.",
  "platform.subprocessors.showNotices": "Notices to tenants: {name}",
  "platform.subprocessors.notices": "Notices to the tenants",
  "platform.subprocessors.noNotices": "No notice yet.",
  "platform.subprocessors.notice.tenant": "Tenant",
  "platform.subprocessors.notice.recipient": "Recipient",
  "platform.subprocessors.notice.status": "State",
  "platform.subprocessors.notice.status.Pending": "being sent",
  "platform.subprocessors.notice.status.Sent": "sent",
  "platform.subprocessors.notice.status.NoRecipient": "no address",
  "platform.subprocessors.notice.sentAt": "Sent on",
  "platform.subprocessors.notice.notSent": "not sent yet",
  "platform.subprocessors.notice.attempts.one": "1 failed attempt",
  "platform.subprocessors.notice.attempts.other": "{count} failed attempts",
  "platform.incidents.title": "Security incidents",
  "platform.incidents.intro":
    "Notification of a personal data breach to a tenant as processor, without undue delay after becoming aware of it (Art. 33(2) GDPR). Proceed according to the incident response plan.",
  "platform.incidents.choose": "Choose the tenant concerned.",
  "platform.incidents.form": "Notification to the tenant",
  "platform.incidents.detectedOn": "Became known on",
  "platform.incidents.detectedAt": "Time",
  "platform.incidents.description": "What happened?",
  "platform.incidents.descriptionHint": "Kind of breach, period and systems concerned.",
  "platform.incidents.dataCategories": "Data and persons concerned",
  "platform.incidents.dataCategoriesHint":
    "Categories and approximate number of persons and records concerned.",
  "platform.incidents.consequences": "Likely consequences",
  "platform.incidents.consequencesHint": "What the breach may mean for the persons concerned.",
  "platform.incidents.measures": "Measures taken and proposed",
  "platform.incidents.measuresHint": "What the operator did and what the customer should do.",
  "platform.incidents.contactEmail": "Address for questions",
  "platform.incidents.contactEmailHint":
    "For example the address of the operator's data protection officer.",
  "platform.incidents.consequence":
    "The notification is e-mailed to the security contact of {name}, without one to the data protection or contract contact. The platform audit keeps what was reported.",
  "platform.incidents.send": "Send notification",
  "platform.incidents.sent": "Notification to {name} queued.",
  "platform.incidents.queued": "Queued on {at}.",
  "platform.contractEnd.title": "End of contract",
  "platform.contractEnd.intro":
    "Terminated tenants, their export window and the deletion. After the export window the nightly run deletes the data; here the deletion can also be started at once.",
  "platform.contractEnd.list": "Tenants after the end of the contract",
  "platform.contractEnd.empty": "No tenant with an ended contract.",
  "platform.contractEnd.since": "Status since",
  "platform.contractEnd.details": "Details",
  "platform.contractEnd.show": "End of contract of {name}",
  "platform.contractEnd.of": "End of contract of {name}",
  "platform.contractEnd.noWindow": "The export window has not been opened yet.",
  "platform.contractEnd.scheduledAt": "Export window opened on",
  "platform.contractEnd.deleteAfter": "Deletion at the earliest on",
  "platform.contractEnd.deletedAt": "Deleted on",
  "platform.contractEnd.report": "Deletion report: deleted records per table",
  "platform.contractEnd.table": "Table",
  "platform.contractEnd.rows": "Records",
  "platform.contractEnd.waiting":
    "The export window runs until {date}; the nightly run deletes the data afterwards.",
  "platform.contractEnd.openWindow": "Open export window",
  "platform.contractEnd.openWindowConsequence":
    "The customer is informed and can export its data until the deletion.",
  "platform.contractEnd.days": "Length of the export window in days",
  "platform.contractEnd.daysHint": "{min} to {max} days.",
  "platform.contractEnd.windowOpened": "Export window for {name} opened.",
  "platform.contractEnd.purge": "Delete permanently",
  "platform.contractEnd.purgeConsequence":
    "All data of the tenant is deleted permanently, including the accounts at the sign-in service that no other tenant uses. This cannot be undone. Only the deletion report remains.",
  "platform.contractEnd.secondFactor":
    "The deletion requires a sign-in with the second factor within the last 10 minutes.",
  "platform.contractEnd.signInAgain":
    "Please sign in again with password and one-time code. You return here afterwards and confirm the deletion once more.",
  "platform.contractEnd.signIn": "Sign in again",
  "platform.contractEnd.purged": "{name} was deleted.",
  "platform.databases.title": "Technology",
  "platform.databases.intro":
    "Databases of the tenants, the move into an own database and the rotation of keys. The platform audit keeps both with their reason.",
  "platform.databases.list": "Databases",
  "platform.databases.key": "Database",
  "platform.databases.tenants": "Tenants",
  "platform.databases.configured": "Set up in operations",
  "platform.databases.isConfigured": "yes",
  "platform.databases.missing": "no",
  "platform.databases.choose": "Choose a tenant for a move or a key rotation.",
  "platform.databases.move": "Move into an own database",
  "platform.databases.inSilo": "The tenant is already in its own database {key}.",
  "platform.databases.noSilo": "No own database is set up in operations.",
  "platform.databases.moveConsequence":
    "The data is copied, the tenant is routed to the new database and its records in the shared database are deleted. Writes wait during the move.",
  "platform.databases.ownConsequence":
    "Zeitavo creates an own database for this tenant on the server, brings it up to date and moves the tenant: the data is copied, the tenant is routed to the new database and its records in the shared database are deleted. Writes wait during the move.",
  "platform.databases.ownStart": "Set up own database and move",
  "platform.databases.target": "Target",
  "platform.databases.chooseTarget": "Choose a database",
  "platform.databases.moveStart": "Start move",
  "platform.databases.moved": "{name} is now in the database {key}.",
  "platform.databases.copied.one": "1 record copied.",
  "platform.databases.copied.other": "{count} records copied.",
  "platform.databases.purged.one": "1 record deleted from the shared database.",
  "platform.databases.purged.other": "{count} records deleted from the shared database.",
  "platform.databases.rotate": "Rotate keys",
  "platform.databases.rotateConsequence":
    "A new data key encrypts the protected values of the tenant again. The previous key is kept to read older backups.",
  "platform.databases.rotateStart": "Rotate keys",
  "platform.databases.rotated": "New key version {version}.",
  "platform.databases.rotationReport.one": "Key version {version}, 1 value encrypted again.",
  "platform.databases.rotationReport.other":
    "Key version {version}, {count} values encrypted again.",
  "platform.nav.auditLog": "Audit log",
  "platform.nav.plans": "Plans",
  "platform.nav.systemStatus": "System status",
  "platform.nav.staffAccounts": "Staff accounts",
  "platform.tenant.auditLog": "View platform audit log",
  "platform.audit.title": "Platform audit log",
  "platform.audit.intro":
    "Every change by operator staff and by the platform's own jobs, newest first. Only the released part of the details is shown: no customer names, no identifiers at the payment provider, no texts of data breach notices.",
  "platform.audit.filter": "Filter the audit log",
  "platform.audit.allTenants": "All tenants",
  "platform.audit.actor": "Person or system user",
  "platform.audit.actorHint":
    "Identifier of the account (suggestions by user name) or a system user such as system:dunning.",
  "platform.audit.from": "From",
  "platform.audit.to": "To (inclusive)",
  "platform.audit.show": "Show",
  "platform.audit.reset": "Reset filters",
  "platform.audit.entries": "Entries",
  "platform.audit.empty": "No entry matches the filters.",
  "platform.audit.older": "Load older entries",
  "platform.audit.tenant": "Tenant",
  "platform.audit.wholePlatform": "Whole platform",
  "platform.audit.comment": "Reason",
  "platform.audit.details": "Details",
  "platform.plans.title": "Plans",
  "platform.plans.intro":
    "Every version of every plan with its state today. Stored versions never change: prices, visibility and functions change with a new version from a later day.",
  "platform.plans.versions": "Versions",
  "platform.plans.noVersions": "No plan version yet.",
  "platform.plans.caption": "{name} ({code})",
  "platform.plans.validFrom": "Valid from",
  "platform.plans.state": "State",
  "platform.plans.state.Past": "Superseded",
  "platform.plans.state.Current": "Applies",
  "platform.plans.state.Scheduled": "Scheduled",
  "platform.plans.name": "Name",
  "platform.plans.code": "Plan code",
  "platform.plans.codeHint":
    "Lower case letters, digits and hyphens, starting with a letter. A new code creates a new plan.",
  "platform.plans.public": "Public",
  "platform.plans.yes": "yes",
  "platform.plans.no": "no",
  "platform.plans.basePrice": "Monthly base price (net)",
  "platform.plans.setupFee": "One-time setup fee, net",
  "platform.plans.pricePerEmployee": "Monthly price per active person (net)",
  "platform.plans.priceHint": "In euros, for example 0 or 12.50.",
  "platform.plans.priceInvalid":
    "Please enter an amount from 0 to 100,000 euros, for example 12.50.",
  "platform.plans.employeeLimit": "People included",
  "platform.plans.employeeLimitHint": "Leave empty for unlimited.",
  "platform.plans.unlimited": "unlimited",
  "platform.plans.entitlements": "Functions",
  "platform.plans.noEntitlements": "Basic functions only",
  "platform.plans.isPublic": "Public: customers can choose the plan themselves",
  "platform.plans.addPlan": "Add a new plan",
  "platform.plans.addVersion": "New version of {name}",
  "platform.plans.consequence":
    "The version applies from its first day to every tenant on this plan, existing customers included, and cannot be changed afterwards. Schedule price increases with the notice period of the terms.",
  "platform.plans.validFromHint":
    "After today and after the latest version of the plan, {date} at the earliest.",
  "platform.plans.reduction":
    "This version contains fewer functions or people than the latest version of the plan. Until the legal question is settled, use a new plan code for it.",
  "platform.plans.confirm": "I have checked the prices, the functions and the first day.",
  "platform.plans.create": "Add version",
  "platform.plans.created": "Version {name} from {date} added.",
  "platform.plans.prices": "Comparison with the online prices",
  "platform.plans.pricesIntro":
    "Comparison of the plans with the prices that can be bought online (Payments:Prices). Check the amounts at the payment provider there.",
  "platform.plans.priceId": "Base fee at the payment provider",
  "platform.plans.perEmployeePriceId": "Price per employee",
  "platform.plans.setupPriceId": "Setup",
  "platform.plans.noPriceId": "none",
  "platform.plans.findings": "Differences",
  "platform.plans.noFindings": "None",
  "platform.plans.noPrices": "No plans and no prices configured.",
  "platform.plans.finding.PublicWithoutPrice":
    "Public but without a price: cannot be bought online",
  "platform.plans.finding.PriceForPlanOnRequest":
    "On request only but with a price: can be bought online",
  "platform.plans.finding.PriceWithoutPlan": "Price for a code without a current version",
  "platform.plans.finding.PerEmployeePriceMissing":
    "No price per employee: active employees are not charged",
  "platform.plans.finding.SetupPriceMissing":
    "Setup fee without a price at the payment provider: it is not charged",
  "platform.plans.finding.SetupPriceWithoutFee":
    "Price for the setup, but the version has no setup fee",
  "platform.plans.finding.BasePriceDiffers":
    "Base fee at the payment provider differs from the catalog (amount, currency, kind or inactive)",
  "platform.plans.finding.PerEmployeePriceDiffers":
    "Price per employee at the payment provider differs from the catalog (amount, currency, kind or inactive)",
  "platform.plans.finding.SetupPriceDiffers":
    "Setup price at the payment provider differs from the catalog (amount, currency, kind or inactive)",
  "platform.plans.finding.ProviderPriceUnavailable":
    "A price could not be read from the payment provider (unknown or no answer)",
  "platform.trial.title": "Trial",
  "platform.trial.endsOn": "Ends on",
  "platform.trial.regularEndsOn": "Regular end",
  "platform.trial.extensions": "Extensions",
  "platform.trial.extensionCount": "{done} so far, {left} still possible",
  "platform.trial.latestEndsOn": "Latest end",
  "platform.trial.notExtendable": "The trial cannot be extended any further.",
  "platform.trial.extend": "Extend trial",
  "platform.trial.consequence":
    "The new end applies at once. The customer is reminded again before the new end.",
  "platform.trial.newEnd": "New last day",
  "platform.trial.newEndHint": "{earliest} at the earliest, {latest} at the latest.",
  "platform.trial.extended": "Trial extended until {date}.",
  "platform.system.title": "System status",
  "platform.system.intro":
    "Background jobs, scheduled runs, e-mail outbox, migrations and the legal review. Without tenants and without personal data.",
  "platform.system.checkedAt": "As of {date}",
  "platform.system.refresh": "Refresh",
  "platform.system.never": "never",
  "platform.system.jobs": "Background jobs",
  "platform.system.jobWindow": "Of all tenants since {date} (7 days).",
  "platform.system.unreachable.one": "1 database did not answer.",
  "platform.system.unreachable.other": "{count} databases did not answer.",
  "platform.system.kind": "Kind",
  "platform.system.lastSucceeded": "Last succeeded",
  "platform.system.lastFailed": "Last failed",
  "platform.system.failed": "Failed for good",
  "platform.system.retrying": "Waiting for a retry",
  "platform.system.noJobs": "No background jobs in the last 7 days.",
  "platform.system.workers": "Scheduled runs",
  "platform.system.worker": "Run",
  "platform.system.lastRun": "Last run",
  "platform.system.noWorkers": "No run recorded yet.",
  "platform.system.outbox": "E-mail outbox",
  "platform.system.pending": "Waiting",
  "platform.system.givenUp": "Given up",
  "platform.system.oldestPending": "Oldest waiting since",
  "platform.system.schemas": "Migrations",
  "platform.system.database": "Database",
  "platform.system.schemaStatus": "State",
  "platform.system.schema.Current": "Current",
  "platform.system.schema.Outdated": "Outdated",
  "platform.system.schema.Unreachable": "Unreachable",
  "platform.system.pendingMigrations": "Missing migrations",
  "platform.system.legalReview": "Legal review",
  "platform.system.register": "Scope register {version}, last reviewed on {date}.",
  "platform.system.procedure": "Legislative procedure",
  "platform.system.checkedOn": "Checked on",
  "platform.system.nextReviewBy": "Next review by",
  "platform.system.overdue": "overdue since {date}",
  "platform.system.lastResult": "Result",
  "platform.system.unchanged": "unchanged",
  "platform.system.changed": "changed",
  "platform.system.noProcedures": "No pending legislative procedure.",
  "platform.staff.title": "Staff accounts",
  "platform.staff.intro":
    "Accounts with a platform role, read from the identity provider on every visit and never stored. Create accounts and assign the roles in the administration console of the identity provider.",
  "platform.staff.list": "Accounts",
  "platform.staff.username": "User name",
  "platform.staff.roles": "Roles",
  "platform.staff.role.PlatformAdmin": "Operations (change)",
  "platform.staff.role.PlatformSupport": "Support (read only)",
  "platform.staff.enabled": "Sign-in",
  "platform.staff.active": "active",
  "platform.staff.disabled": "disabled",
  "platform.staff.secondFactor": "Second factor",
  "platform.staff.secondFactorSet": "set up",
  "platform.staff.secondFactorMissing": "missing, asked for at the next sign-in",
  "platform.staff.console": "Console",
  "platform.staff.openAccount": "Open {name} in the console",
  "platform.staff.openConsole": "Open the administration console",
  "platform.staff.tunnel": "Reachable through the admin tunnel only.",
  "platform.staff.noConsole": "The link to the administration console is not configured.",
  "platform.staff.empty": "No account has a platform role.",
  "error.TT-1016": "A change of this subprocessor is already announced. Wait until it applies.",
  "error.TT-3025":
    "This is possible only once the contract has ended and the export window is over.",
  "error.TT-3029": "The target database has another schema or already holds data of this tenant.",
  "error.TT-3039":
    "A version with this label or date is already published with other content. Publish a new version.",
  "error.TT-3057": "The credit note exceeds the amount of the invoice not credited yet.",
  "error.TT-3061": "The tenant has no address for notices. Inform the customer by other means.",
  "error.TT-3490":
    "This step requires a sign-in with the second factor within the last 10 minutes. Please sign in again.",
  "error.TT-3015": "This tenant is already registered.",
  "error.TT-3019":
    "Single sign-on is not possible: the tenant is terminated or has no organization in the sign-in service.",
  "error.TT-3020": "One of the e-mail domains already belongs to another tenant.",
  "error.TT-3027": "This own database is not set up in operations.",
  "error.TT-3028":
    "This server does not create own databases. Set the database up in operations and choose it as the target.",
  "error.TT-3033":
    "With a very high protection need the tenant must be created in an own database.",
  "platform.auditAction.Tenant.Registered": "Tenant registered",
  "platform.auditAction.Tenant.PlanChanged": "Plan changed",
  "platform.auditAction.Tenant.ProtectionAssessed": "Protection need assessed",
  "platform.auditAction.Tenant.Active": "Status “Active”",
  "platform.auditAction.Tenant.PastDue": "Status “Payment overdue”",
  "platform.auditAction.Tenant.Suspended": "Status “Suspended”",
  "platform.auditAction.Tenant.Terminated": "Status “Terminated”",
  "platform.auditAction.Tenant.ExportWindow": "Status “Export period”",
  "platform.auditAction.Tenant.Deleted.Status": "Status “Deleted”",
  "platform.auditAction.Tenant.Deleted": "Tenant deleted",
  "platform.auditAction.Tenant.IdentitiesDeleted": "Accounts deleted in the identity provider",
  "platform.auditAction.Tenant.OrganizationDeleted":
    "Organization deleted in the identity provider",
  "platform.auditAction.Tenant.OffboardingScheduled": "Contract end scheduled",
  "platform.auditAction.Tenant.OffboardingCancelled": "Contract end cancelled",
  "platform.auditAction.Tenant.ExportWindowNotice": "Export period notice sent",
  "platform.auditAction.Tenant.SingleSignOnConfigured": "Company sign-in configured",
  "platform.auditAction.Tenant.SingleSignOnRemoved": "Company sign-in removed",
  "platform.auditAction.Tenant.MoveStarted": "Move to own database started",
  "platform.auditAction.Tenant.MoveAborted": "Move to own database aborted",
  "platform.auditAction.Tenant.MovedToSilo": "Moved to own database",
  "platform.auditAction.Tenant.KeyRotated": "Key renewed",
  "platform.auditAction.Tenant.SecurityIncidentNotified": "Security incident notified",
  "platform.auditAction.Tenant.TrialExtended": "Trial extended",
  "platform.auditAction.Onboarding.Started": "Onboarding started",
  "platform.auditAction.Onboarding.OrganizationCreated":
    "Organization created in the identity provider",
  "platform.auditAction.Onboarding.AdministratorCreated": "Administration created",
  "platform.auditAction.Onboarding.TenantProvisioned": "Tenant provisioned",
  "platform.auditAction.Onboarding.Registered": "Entered in the tenant directory",
  "platform.auditAction.Onboarding.AdministratorAccountCreated": "Administrator account created",
  "platform.auditAction.Onboarding.InvitationSent": "Invitation sent",
  "platform.auditAction.Onboarding.InvitationFailed": "Invitation failed",
  "platform.auditAction.Onboarding.RolledBack": "Onboarding rolled back",
  "platform.auditAction.Onboarding.RollbackFailed": "Rollback of the onboarding failed",
  "platform.auditAction.Plan.VersionCreated": "Plan version added",
  "platform.auditAction.Invoice.Issued": "Invoice issued",
  "platform.auditAction.Invoice.CreditNoteIssued": "Credit note issued",
  "platform.auditAction.Payment.CustomerCreated": "Customer created at the payment provider",
  "platform.auditAction.Payment.Mirrored": "Payment state taken over",
  "platform.auditAction.Payment.Mismatch": "Difference in the payment reconciliation",
  "platform.auditAction.Payment.UsageReported": "Active employees reported to the payment provider",
  "platform.auditDetail.Status": "Status",
  "platform.auditDetail.PlanId": "Plan ID",
  "platform.auditDetail.PlanCode": "Plan",
  "platform.auditDetail.Isolation": "Data isolation",
  "platform.auditDetail.DatabaseKey": "Database",
  "platform.auditDetail.ProtectionNeed": "Protection need",
  "platform.auditDetail.Step": "Step",
  "platform.auditDetail.Kind": "Kind",
  "platform.auditDetail.Number": "Number",
  "platform.auditDetail.NetCents": "Net (cents)",
  "platform.auditDetail.TaxCents": "VAT (cents)",
  "platform.auditDetail.GrossCents": "Gross (cents)",
  "platform.auditDetail.TaxTreatment": "Tax treatment",
  "platform.auditDetail.Protocol": "Protocol",
  "platform.auditDetail.Changed": "Changed",
  "platform.auditDetail.Month": "Month",
  "platform.auditDetail.Quantity": "Quantity",
  "platform.auditDetail.PaymentMethod": "Payment method",
  "platform.auditDetail.DetectedAt": "Detected at",
  "platform.auditDetail.PreviousTrialEndsOn": "Previous end of the trial",
  "platform.auditDetail.TrialEndsOn": "End of the trial",
  "platform.auditDetail.TrialExtensions": "Extensions",
  "platform.auditDetail.Comment": "Reason",
  "platform.auditDetail.Name": "Name",
  "platform.auditDetail.IsPublic": "Publicly available",
  "platform.auditDetail.ValidFrom": "Valid from",
  "platform.auditDetail.BasePriceCents": "Base price (cents)",
  "platform.auditDetail.PricePerActiveEmployeeCents": "Price per active person (cents)",
  "platform.auditDetail.Currency": "Currency",
  "platform.auditDetail.EmployeeLimit": "Persons included",
  "platform.auditDetail.Entitlements": "Functions",
  "platform.jobKind.PublicApiWebhooks": "Webhooks of the public API",
  "platform.jobKind.NightlyCompliance": "Nightly rule check",
  "platform.jobKind.RecordingReminders": "Recording reminders",
  "platform.jobKind.AuditAnchor": "Anchor of the audit trail",
  "platform.jobKind.Retention": "Retention periods",
  "platform.jobKind.UsageMetering": "Usage metering",
  "platform.jobKind.DataEncryption": "Data encryption",
  "platform.jobKind.ProjectAttributionReminders": "Project attribution reminders",
  "platform.jobKind.TenantDataExport": "Data export of the tenant",
  "platform.jobKind.LeaveNotices": "Leave notices",
  "platform.jobKind.AbsenceNotifications": "Absence notifications",
  "platform.jobKind.RosterNotifications": "Roster notifications",
  "platform.jobKind.ProjectBudgetAlerts": "Project budget alerts",
  "platform.worker.PlatformWorker": "Platform (status, dunning, deadlines, deletions)",
  "platform.worker.EmailOutboxWorker": "E-mail delivery",
  // F-86: steps of the platform run, new audit keys and known detail values (ADR-0210).
  "platform.system.steps": "Steps: {worker}",
  "platform.system.stepsIntro":
    "If a step fails, the other steps still run; only the steps that need it are skipped and run in the next run.",
  "platform.system.step": "Step",
  "platform.system.stepResult": "In the last run",
  "platform.workerStep.SubscriptionSync": "Take over plan and service state",
  "platform.workerStep.ExportWindow": "Open export windows, send notices",
  "platform.workerStep.Offboarding": "Delete tenants after the export window",
  "platform.workerStep.SubprocessorNotices": "Send notices about subprocessors",
  "platform.workerStep.Dunning": "Dunning and suspensions",
  "platform.workerStep.TrialReminders": "Reminders before the end of a trial",
  "platform.workerStep.UnfinishedSignups": "Delete unfinished registrations",
  "platform.workerStep.AuditRetention": "Delete platform audit entries after their retention",
  "platform.workerStepResult.Succeeded": "Succeeded",
  "platform.workerStepResult.Failed": "Failed",
  "platform.workerStepResult.NotRun": "Not run",
  "platform.workerStepResult.Skipped": "Skipped",
  "platform.auditDetail.Rows": "Deleted records",
  "platform.auditDetail.PendingAccounts": "Accounts still open",
  "platform.auditDetail.Deleted": "Deleted accounts",
  "platform.auditDetail.KeptAccounts": "Kept accounts",
  "platform.auditDetail.Organization": "Organization in the sign-in service",
  "platform.auditDetail.Reason": "Reason",
  "platform.auditDetail.DeleteAfter": "Deletion from",
  "platform.auditDetail.Version": "Key version",
  "platform.auditDetail.Resealed": "Keys encrypted again",
  "platform.auditDetail.MovingToDatabaseKey": "Target database",
  "platform.auditDetail.CorrectedInvoiceNumber": "Corrected invoice",
  "platform.auditValue.true": "Yes",
  "platform.auditValue.false": "No",
  "platform.auditValue.Kind.Invoice": "Invoice",
  "platform.auditValue.Kind.CreditNote": "Credit note",
  "platform.auditValue.Kind.Opened": "Export window opened",
  "platform.auditValue.Kind.Closing": "Export window closing soon",
  "platform.auditValue.TaxTreatment.Domestic19": "Domestic, 19 % VAT",
  "platform.auditValue.TaxTreatment.ReverseChargeEu": "Reverse charge (EU)",
  "platform.auditValue.Reason.Reactivated": "Reactivated",
  "error.TT-3500": "The tenant is not in its trial. Only a running trial can be extended.",
  "error.TT-3501":
    "This day is not allowed: the new end must lie after the current end, not before today and not after the latest end.",
  "error.TT-3502": "The trial has already been extended as often as allowed.",
  "error.TT-3503":
    "The version must start after today and after the latest version of the plan. Stored versions stay unchanged.",
  "error.TT-3504":
    "The staff accounts cannot be shown: the administration of the identity provider is not configured.",
  "error.TT-3505":
    "The identity provider refused or did not answer the list of staff accounts. Check the role “view-realm” of the service account.",
  "error.TT-3506":
    "The new version includes fewer functions or fewer employees than the latest version of the plan. For a smaller scope, add a new plan with its own code.",
  "error.TT-3507":
    "The databases are being updated right now. Set up the own database when the update has finished.",
};
